<template>
  <v-main>
    <v-container fluid class="dash pa-4">
      <!-- Welcome -->
      <div class="mb-5">
        <div class="welcome">Welcome back, {{ firstName }} <span class="wave">👋</span></div>
        <div class="welcome-sub">Here's what's happening at Vastrosa right now</div>
      </div>

      <v-alert v-if="error" type="warning" dense text class="mb-5">{{ error }}</v-alert>

      <!-- Stat cards -->
      <v-row dense>
        <v-col v-for="c in statCards" :key="c.label" cols="12" sm="6" lg="3">
          <div class="stat-card" :class="c.theme" @click="$router.push(c.to)">
            <div class="stat-top">
              <span class="stat-label">{{ c.label }}</span>
              <span class="stat-icon"><v-icon small color="white">{{ c.icon }}</v-icon></span>
            </div>
            <div class="stat-value">{{ c.value }}</div>
            <span class="stat-pill">{{ c.hint }}</span>
          </div>
        </v-col>
      </v-row>

      <!-- Sales trend + order outcomes -->
      <v-row dense>
        <v-col cols="12" lg="8">
          <div class="panel">
            <div class="panel-title">Sales &mdash; Last 30 Days</div>
            <dash-chart v-if="data" type="line" :data="trendData" :options="trendOptions" :height="230" />
          </div>
        </v-col>
        <v-col cols="12" lg="4">
          <div class="panel">
            <div class="panel-title">Order Outcomes &mdash; All Time</div>
            <dash-chart v-if="data" type="doughnut" :data="outcomeData" :options="donutOptions" :plugins="donutPlugins" :height="230" />
          </div>
        </v-col>
      </v-row>

      <!-- Weekly orders + payment status -->
      <v-row dense>
        <v-col cols="12" lg="8">
          <div class="panel">
            <div class="panel-title">Orders Overview</div>
            <div class="big-number">{{ weeklyTotal }}</div>
            <div class="panel-sub">Orders placed &mdash; last 4 weeks</div>
            <dash-chart v-if="data" type="line" :data="weeksData" :options="weeksOptions" :height="170" />
            <v-row class="text-center mt-2" no-gutters>
              <v-col v-for="o in outcomeSummary" :key="o.label" cols="4">
                <div class="mini-label">{{ o.label }}</div>
                <div class="mini-value" :style="{ color: o.color }">{{ o.value }}</div>
              </v-col>
            </v-row>
          </div>
        </v-col>
        <v-col cols="12" lg="4">
          <div class="panel">
            <div class="panel-title">Payment Status</div>
            <div class="panel-sub">Current breakdown of all sales</div>
            <dash-chart v-if="data" type="doughnut" :data="paymentData" :options="paymentOptions" :plugins="donutPlugins" :height="170" />
            <div v-for="p in paymentRows" :key="p.label" class="legend-row">
              <span class="dot" :style="{ background: p.color }"></span>
              <span>{{ p.label }}</span>
              <span class="ml-auto">{{ p.value }}</span>
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- Users by role + recent orders -->
      <v-row dense>
        <v-col cols="12" lg="5">
          <div class="panel">
            <div class="panel-title">Users by Role</div>
            <dash-chart v-if="data" type="bar" :data="rolesData" :options="rolesOptions" :plugins="barPlugins" :height="270" />
          </div>
        </v-col>
        <v-col cols="12" lg="7">
          <div class="panel">
            <div class="d-flex align-center">
              <div class="panel-title">Recent Orders</div>
              <v-spacer />
              <v-btn text small color="primary" @click="$router.push('/orders')">View all</v-btn>
            </div>
            <v-simple-table class="recent">
              <thead>
                <tr>
                  <th class="text-left">Order</th>
                  <th class="text-left">Customer</th>
                  <th class="text-left">Source</th>
                  <th class="text-right">Amount</th>
                  <th class="text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="o in recent" :key="o.id" class="clickable" @click="$router.push('/orders/' + o.id)">
                  <td>{{ o.order_no }}</td>
                  <td>{{ o.customer }}</td>
                  <td>{{ o.source }}</td>
                  <td class="text-right">{{ money(o.amount) }}</td>
                  <td><span class="status-chip" :class="statusClass(o.status)">{{ o.status }}</span></td>
                </tr>
                <tr v-if="!recent.length">
                  <td colspan="5" class="text-center grey--text py-6">{{ loading ? 'Loading...' : 'No orders yet' }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";
import DashChart from "./DashChart.vue";

const GREEN = "#16a34a";
const AMBER = "#f5a524";
const RED = "#e5484d";
const BLUE = "#3b82f6";
const ORANGE = "#f26b2b";

export default {
  name: "HomePage",
  components: { DashChart },

  data() {
    return { data: null, loading: false, error: "" };
  },

  computed: {
    user() {
      return this.$store.getters["auth/user"];
    },
    firstName() {
      return (this.user && this.user.first_name) || "Admin";
    },
    cards() {
      return this.data ? this.data.cards : {};
    },
    recent() {
      return this.data ? this.data.recent.slice(0, 6) : [];
    },

    statCards() {
      const c = this.cards;
      const n = (v) => Number(v || 0).toLocaleString("en-IN");
      return [
        { label: "TOTAL ORDERS", icon: "mdi-cart-outline", value: n(c.total_orders), hint: "all-time", theme: "t-cyan", to: "/orders" },
        { label: "PENDING ORDERS", icon: "mdi-clock-outline", value: n(c.pending_orders), hint: "not yet delivered", theme: "t-orange", to: "/orders" },
        { label: "TODAY'S SALES", icon: "mdi-currency-inr", value: this.money(c.today_sales), hint: `${n(c.today_orders)} orders today`, theme: "t-green", to: "/orders" },
        { label: "PENDING PAYMENTS", icon: "mdi-cash-clock", value: this.money(c.pending_payments), hint: `${n(c.pending_payment_orders)} orders unpaid`, theme: "t-indigo", to: "/reporting" },
        { label: "LOW STOCK ITEMS", icon: "mdi-package-variant-closed-remove", value: n(c.low_stock), hint: `${n(c.out_of_stock)} out of stock`, theme: "t-crimson", to: "/company-stocks" },
        { label: "PAYABLES DUE", icon: "mdi-truck-delivery-outline", value: this.money(c.payables), hint: `${n(c.payable_bills)} bills outstanding`, theme: "t-teal", to: "/purchase-bills" },
        { label: "REVENUE (30 DAYS)", icon: "mdi-chart-line", value: this.money(c.revenue_30d), hint: "gross, last 30 days", theme: "t-purple", to: "/reporting" },
      ];
    },

    // ── Sales line chart ────────────────────────────────────────────────
    trendData() {
      const t = this.data.trend;
      const set = (label, key, color) => ({
        label,
        data: t.map((d) => d[key]),
        borderColor: color,
        backgroundColor: color + "22",
        fill: key === "sales",
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 4,
        borderWidth: 2,
      });
      return {
        labels: t.map((d) => this.shortDate(d.date)),
        datasets: [set("Sales", "sales", GREEN), set("Collected", "collected", ORANGE)],
      };
    },
    trendOptions() {
      return {
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: {
            position: "top",
            labels: {
              usePointStyle: true,
              boxWidth: 8,
              boxHeight: 8,
              padding: 18,
              font: { size: 12 },
              generateLabels: (chart) =>
                chart.data.datasets.map((ds, i) => ({ text: ds.label, fillStyle: ds.borderColor, strokeStyle: ds.borderColor, pointStyle: "circle", lineWidth: 0, hidden: !chart.isDatasetVisible(i), datasetIndex: i })),
            },
          },
          tooltip: { callbacks: { label: (c) => ` ${c.dataset.label}: ${this.money(c.parsed.y)}` } },
        },
        scales: {
          x: { grid: { display: false }, ticks: { maxTicksLimit: 12, font: { size: 11 } } },
          y: { beginAtZero: true, ticks: { maxTicksLimit: 6, callback: (v) => this.money(v), font: { size: 11 } }, grid: { color: "#eef0f4" } },
        },
      };
    },

    // ── Donuts ──────────────────────────────────────────────────────────
    outcomeData() {
      const o = this.data.outcomes;
      return { labels: o.map((x) => x.label), datasets: [{ data: o.map((x) => x.value), backgroundColor: [GREEN, AMBER, RED], borderWidth: 2, borderColor: "#fff" }] };
    },
    paymentRows() {
      return this.data
        ? this.data.payment.map((p, i) => ({ ...p, color: [GREEN, AMBER, RED][i] }))
        : [];
    },
    paymentData() {
      const p = this.data.payment;
      return { labels: p.map((x) => x.label), datasets: [{ data: p.map((x) => x.value), backgroundColor: [GREEN, AMBER, RED], borderWidth: 2, borderColor: "#fff" }] };
    },
    donutOptions() {
      return { cutout: "62%", plugins: { legend: { position: "bottom", labels: { usePointStyle: true, boxWidth: 8, font: { size: 12 } } } } };
    },
    paymentOptions() {
      return { cutout: "62%", plugins: { legend: { display: false } } };
    },
    // "Total N" in the hole and a % on each slice
    donutPlugins() {
      return [
        {
          id: "donutLabels",
          afterDatasetsDraw(chart) {
            const { ctx } = chart;
            const values = chart.data.datasets[0].data;
            const total = values.reduce((a, b) => a + b, 0);
            const meta = chart.getDatasetMeta(0);
            ctx.save();
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            meta.data.forEach((arc, i) => {
              if (!total || !values[i] || values[i] / total < 0.06) return;
              const pos = arc.tooltipPosition();
              ctx.fillStyle = "#fff";
              ctx.font = "bold 11px Roboto, sans-serif";
              ctx.fillText(Math.round((values[i] / total) * 100) + "%", pos.x, pos.y);
            });
            const x = (chart.chartArea.left + chart.chartArea.right) / 2;
            const y = (chart.chartArea.top + chart.chartArea.bottom) / 2;
            ctx.fillStyle = "#4b5563";
            ctx.font = "12px Roboto, sans-serif";
            ctx.fillText("Total", x, y - 10);
            ctx.fillStyle = "#111827";
            ctx.font = "500 19px Roboto, sans-serif";
            ctx.fillText(total, x, y + 10);
            ctx.restore();
          },
        },
      ];
    },

    // ── Weekly orders ───────────────────────────────────────────────────
    weeklyTotal() {
      return this.data ? this.data.weeks.reduce((a, w) => a + w.orders, 0) : 0;
    },
    weeksData() {
      const w = this.data.weeks;
      return {
        labels: w.map((x) => x.label),
        datasets: [{ label: "Orders", data: w.map((x) => x.orders), borderColor: BLUE, backgroundColor: BLUE + "22", fill: true, tension: 0.4, pointRadius: 4, pointBackgroundColor: BLUE, borderWidth: 2 }],
      };
    },
    weeksOptions() {
      return {
        plugins: { legend: { display: false } },
        scales: {
          x: { grid: { display: false }, ticks: { font: { size: 11 } } },
          y: { beginAtZero: true, ticks: { precision: 0, font: { size: 11 } }, grid: { color: "#eef0f4" } },
        },
      };
    },
    outcomeSummary() {
      const o = this.data ? this.data.outcomes : [];
      const colors = [GREEN, AMBER, RED];
      return o.map((x, i) => ({ ...x, color: colors[i] }));
    },

    // ── Users by role ───────────────────────────────────────────────────
    rolesData() {
      const r = this.data.usersByRole;
      const colors = ["#3b82f6", "#f26b2b", "#10b981", "#8b5cf6", "#f5a524", "#ec4899", "#06b6d4", "#84cc16", "#64748b"];
      return { labels: r.map((x) => x.label), datasets: [{ data: r.map((x) => x.value), backgroundColor: r.map((_, i) => colors[i % colors.length]), borderRadius: 6, maxBarThickness: 56 }] };
    },
    rolesOptions() {
      return {
        plugins: { legend: { display: false } },
        scales: {
          x: { grid: { display: false }, ticks: { font: { size: 10 }, maxRotation: 40, minRotation: 0 } },
          y: { beginAtZero: true, ticks: { precision: 0, font: { size: 11 } }, grid: { color: "#eef0f4" } },
        },
      };
    },
    barPlugins() {
      return [
        {
          id: "barValues",
          afterDatasetsDraw(chart) {
            const { ctx } = chart;
            ctx.save();
            ctx.fillStyle = "#fff";
            ctx.font = "bold 11px Roboto, sans-serif";
            ctx.textAlign = "center";
            chart.getDatasetMeta(0).data.forEach((bar, i) => {
              const v = chart.data.datasets[0].data[i];
              if (bar.height > 13) ctx.fillText(v, bar.x, bar.y + (bar.base - bar.y) / 2 + 4);
            });
            ctx.restore();
          },
        },
      ];
    },
  },

  mounted() {
    this.load();
  },

  methods: {
    load() {
      this.loading = true;
      this.error = "";
      axios
        .get("dashboard/overview")
        .then((r) => {
          if (r.data.success) this.data = r.data.data;
        })
        .catch((e) => {
          this.error = (e.response && e.response.data && e.response.data.message) || "Could not load the dashboard.";
        })
        .finally(() => (this.loading = false));
    },
    money(v) {
      return "₹" + Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
    },
    shortDate(d) {
      return new Date(d + "T00:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
    },
    statusClass(s) {
      if (s === "Delivered") return "s-green";
      if (s === "Cancelled" || s === "Returned") return "s-red";
      if (s === "Pending") return "s-amber";
      return "s-blue";
    },
  },
};
</script>

<style scoped>
.dash {
  background: #f5f6fa;
  min-height: 100%;
}
.welcome {
  font-size: 24px;
  font-weight: 500;
  color: #1f2937;
}
.wave {
  font-size: 22px;
}
.welcome-sub {
  color: #6b7280;
  font-size: 13px;
  margin-top: 2px;
}

/* ── Stat cards ── */
.stat-card {
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  padding: 14px 16px;
  color: #fff;
  min-height: 0;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.stat-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.18);
}
.stat-card::after {
  content: "";
  position: absolute;
  right: -22px;
  bottom: -34px;
  width: 96px;
  height: 96px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.14);
}
.stat-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.stat-label {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.08em;
}
.stat-icon {
  width: 26px;
  height: 26px;
  border-radius: 7px;
  background: rgba(255, 255, 255, 0.22);
  display: flex;
  align-items: center;
  justify-content: center;
}
.stat-value {
  font-size: 26px;
  font-weight: 700;
  line-height: 1.1;
  margin: 10px 0 9px;
}
.stat-pill {
  position: relative;
  z-index: 1;
  display: inline-block;
  font-size: 11px;
  padding: 2px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.22);
}
.t-cyan { background: linear-gradient(135deg, #00acc1, #0e8fa6); }
.t-orange { background: linear-gradient(135deg, #f59e3a, #dc8420); }
.t-green { background: linear-gradient(135deg, #2ecc71, #1fa75a); }
.t-indigo { background: linear-gradient(135deg, #3f51b5, #2b3a95); }
.t-crimson { background: linear-gradient(135deg, #d81b60, #b0124a); }
.t-teal { background: linear-gradient(135deg, #00897b, #006b61); }
.t-purple { background: linear-gradient(135deg, #7e57c2, #5e35b1); }

/* ── Panels ── */
.panel {
  background: #fff;
  border-radius: 12px;
  padding: 16px 18px;
  height: 100%;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06);
}
.panel-title {
  font-size: 15px;
  font-weight: 500;
  color: #1f2937;
  margin-bottom: 6px;
}
.panel-sub {
  color: #6b7280;
  font-size: 12px;
  margin-bottom: 6px;
}
.big-number {
  font-size: 24px;
  font-weight: 500;
  color: #111827;
  line-height: 1.1;
}
.mini-label {
  color: #6b7280;
  font-size: 12px;
}
.mini-value {
  font-size: 17px;
  font-weight: 600;
}
.legend-row {
  display: flex;
  align-items: center;
  padding: 4px 0;
  font-size: 13px;
  color: #374151;
}
.dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  margin-right: 10px;
}

/* ── Recent orders table ── */
.recent th {
  font-size: 11px !important;
  height: 34px !important;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #4b5563 !important;
}
.recent .clickable {
  cursor: pointer;
}
.recent td {
  font-size: 12.5px !important;
  height: 38px !important;
}
.status-chip {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 6px;
  background: #eceff1;
  color: #455a64;
}
.s-green { background: #e6f7ec; color: #1a8f43; }
.s-amber { background: #fff3dd; color: #b26a00; }
.s-red { background: #ffe9e9; color: #c62828; }
.s-blue { background: #e7f0ff; color: #1d5fd6; }
</style>
